/**
 * 工務店グループ（A-07 の中・**FR-129/D-86**）＝現場アドレス帳を共有する単位。
 *
 * ★**全員対等**（管理者ロールを作らない）＝現場を共有している以上、誰でも参加コードを
 *   出し直せて、誰でも除名できる。代わりに**取り返しのつかない操作は必ず確認を挟む**。
 * ★**参加コードは本人たちにだけ見える**（卸にも運営にも出さない）。漏れたら「再発行」で
 *   旧コードは即失効する。
 * ★**合流しても、いま登録している現場は元のグループに残る**（D-86 の増補・05 §6.2 A-07）。
 *   移管しないのは、2つのグループにまたがって復号・再暗号化することになり、RLS の
 *   逃げ道を増やす対価が高いから。**移らないことを合流の後に知ると「現場が消えた」に見える**
 *   ので、送信の前に必ず書く（取り返しの付きにくい操作の前に、何が起きないかを書く）。
 */

import type { BuilderCompany, partnerApi } from "@kenzai/api-client";
import { Badge, Card, ConfirmDialog, ErrorNote, Spinner } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { SmallButton } from "./AddressBook";
import { Input } from "./formKit";

type Client = ReturnType<typeof partnerApi>;

/** 見た目の正規化（大文字化・空白/ハイフン除去）。判定はサーバ側が正。 */
function normalizeCode(raw: string): string {
  return raw.replace(/[\s-]/g, "").toUpperCase().slice(0, 8);
}

type Pending =
  | { kind: "rotate" }
  | { kind: "leave" }
  | { kind: "remove"; builderUserId: number; label: string }
  | { kind: "join"; code: string };

export function BuilderGroup({ client }: { client: Client }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["builder-company"], queryFn: () => client.company() });

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (q.data) setName(q.data.name);
  }, [q.data]);

  const done = async (next: BuilderCompany | { status: string }) => {
    setPending(null);
    setCode("");
    if ("company_id" in next) qc.setQueryData(["builder-company"], next);
    // 会社が変わると**見える現場が変わる**（RLS の会社一致）＝アドレス帳も取り直す
    await qc.invalidateQueries({ queryKey: ["builder-sites"] });
    await qc.invalidateQueries({ queryKey: ["builder-company"] });
  };

  const rename = useMutation({
    mutationFn: () => client.renameCompany(name.trim()),
    onSuccess: (c) => {
      qc.setQueryData(["builder-company"], c);
      setSaved(true);
    },
  });
  const act = useMutation({
    mutationFn: (p: Pending) => {
      if (p.kind === "rotate") return client.rotateShareCode();
      if (p.kind === "leave") return client.leaveCompany();
      if (p.kind === "remove") return client.removeCompanyMember(p.builderUserId);
      return client.joinCompany(normalizeCode(p.code));
    },
    onSuccess: done,
  });

  if (q.isLoading) return <Spinner />;
  if (q.isError) {
    return (
      <Card>
        <ErrorNote error={q.error} />
      </Card>
    );
  }
  const company = q.data!;

  return (
    <Card>
      <h2 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>工務店グループ</h2>
      <p
        style={{
          margin: "0 0 var(--kz-space-3)",
          fontSize: "var(--kz-font-size-sm)",
          color: "var(--kz-color-ink-muted)",
        }}
      >
        同じグループの方とは、現場（お届け先）を共有します。注文の履歴は共有しません。
      </p>

      {rename.isError && <ErrorNote error={rename.error} />}
      {act.isError && <ErrorNote error={act.error} />}

      <Input
        label="グループ名"
        value={name}
        onChange={(v) => {
          setName(v);
          setSaved(false);
        }}
        maxLength={100}
        data-testid="group-name"
      />
      <div style={{ display: "flex", gap: "var(--kz-space-2)", marginBottom: "var(--kz-space-4)" }}>
        <SmallButton
          testId="group-rename"
          primary
          disabled={rename.isPending || name.trim() === ""}
          onClick={() => rename.mutate()}
        >
          名前を保存
        </SmallButton>
        {saved && (
          <span style={{ alignSelf: "center", color: "var(--kz-status-ok)", fontSize: "var(--kz-font-size-sm)" }}>
            保存しました。
          </span>
        )}
      </div>

      <h3 style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>参加コード</h3>
      <p style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
        このコードを渡すと、その方が同じグループに入れます。渡した相手には、現場の住所・連絡先が見えます。
      </p>
      <p
        className="kz-num"
        data-testid="group-share-code"
        style={{ margin: "0 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-xl)", fontWeight: 700, letterSpacing: "0.2em" }}
      >
        {company.share_code}
      </p>
      <SmallButton testId="group-rotate" onClick={() => setPending({ kind: "rotate" })}>
        参加コードを作り直す
      </SmallButton>

      <h3 style={{ margin: "var(--kz-space-4) 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
        メンバー（{company.members.length}人）
      </h3>
      <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
        {company.members.map((m) => (
          <div
            key={m.builder_user_id}
            data-testid={`group-member-${m.builder_user_id}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--kz-space-2)",
              border: "1px solid var(--kz-color-border)",
              borderRadius: "var(--kz-radius-sm)",
              padding: "var(--kz-space-2) var(--kz-space-3)",
              flexWrap: "wrap",
            }}
          >
            <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{m.display_name ?? "お名前未登録の方"}</span>
            {m.is_me && <Badge tone="primary">自分</Badge>}
            {!m.is_me && (
              <span style={{ marginLeft: "auto" }}>
                <SmallButton
                  testId={`group-remove-${m.builder_user_id}`}
                  onClick={() =>
                    setPending({
                      kind: "remove",
                      builderUserId: m.builder_user_id,
                      label: m.display_name ?? "この方",
                    })
                  }
                >
                  グループから外す
                </SmallButton>
              </span>
            )}
          </div>
        ))}
      </div>

      <h3 style={{ margin: "var(--kz-space-4) 0 var(--kz-space-2)", fontSize: "var(--kz-font-size-md)" }}>
        参加コードで合流する
      </h3>
      <p
        data-testid="group-join-notice"
        style={{
          margin: "0 0 var(--kz-space-2)",
          padding: "var(--kz-space-3)",
          background: "var(--kz-status-warn-weak)",
          borderRadius: "var(--kz-radius-sm)",
          fontSize: "var(--kz-font-size-sm)",
        }}
      >
        いま登録している現場は元のグループに残ります（新しいグループには移りません）。
        合流したあとは、新しいグループの現場が見えるようになります。
      </p>
      <Input
        label="参加コード（8桁）"
        value={code}
        onChange={(v) => setCode(normalizeCode(v))}
        data-testid="group-join-code"
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
      />
      <SmallButton
        testId="group-join"
        disabled={act.isPending || normalizeCode(code).length !== 8}
        onClick={() => setPending({ kind: "join", code })}
      >
        このコードで合流する
      </SmallButton>

      {company.members.length > 1 && (
        <div style={{ marginTop: "var(--kz-space-4)" }}>
          <SmallButton testId="group-leave" onClick={() => setPending({ kind: "leave" })}>
            このグループから抜ける
          </SmallButton>
        </div>
      )}

      {pending && (
        <ConfirmDialog
          title={
            pending.kind === "rotate"
              ? "参加コードを作り直しますか？"
              : pending.kind === "leave"
                ? "このグループから抜けますか？"
                : pending.kind === "remove"
                  ? `${pending.label} をグループから外しますか？`
                  : "このコードで合流しますか？"
          }
          body={
            <p style={{ margin: 0 }}>
              {pending.kind === "rotate" &&
                "いまのコードは使えなくなります。まだ入っていない方には、新しいコードをお渡しください。"}
              {pending.kind === "leave" &&
                "抜けると、このグループの現場（お届け先）は見えなくなります。ご自身のご注文の履歴は残ります。"}
              {pending.kind === "remove" &&
                "外した方には、このグループの現場（お届け先）が見えなくなります。ご本人のご注文の履歴は残ります。"}
              {pending.kind === "join" &&
                "いま登録している現場は元のグループに残ります（新しいグループには移りません）。"}
            </p>
          }
          confirmLabel={
            pending.kind === "rotate"
              ? "作り直す"
              : pending.kind === "leave"
                ? "抜ける"
                : pending.kind === "remove"
                  ? "外す"
                  : "合流する"
          }
          tone={pending.kind === "join" ? "primary" : "danger"}
          busy={act.isPending}
          onConfirm={() => act.mutate(pending)}
          onCancel={() => setPending(null)}
        />
      )}
    </Card>
  );
}
